import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Modal,
    TextField,
    Button,
    Snackbar,
    Alert,
    Paper,
} from '@mui/material';
import { Visibility, Add, Close } from '@mui/icons-material';
import axios from 'axios';
import moment from 'moment';

const BaixasMedicas = () => {
    const [empregados, setEmpregados] = useState([]);
    const [selectedEmpregado, setSelectedEmpregado] = useState(null);
    const [baixasMedicas, setBaixasMedicas] = useState([]);
    const [openViewModal, setOpenViewModal] = useState(false);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [newBaixaMedica, setNewBaixaMedica] = useState({
        data_inicio: moment().format('YYYY-MM-DD'),
        data_fim: moment().format('YYYY-MM-DD'),
        descricao: '',
        documento_medico: null,
    });
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

    const apiBaseURL = 'https://plasticemployers.duckdns.org';      // IP da rede local


    useEffect(() => {
        fetchEmpregados();
    }, []);

    const fetchEmpregados = async () => {
        const token = localStorage.getItem('access');
        try {
            const response = await axios.get(`${apiBaseURL}/api/empregados/`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const empregadosComBaixas = await Promise.all(response.data.map(async (empregado) => {
                const baixaResponse = await axios.get(`${apiBaseURL}/api/baixas-medicas/?empregado=${empregado.id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                return {
                    ...empregado,
                    isDeBaixa: baixaResponse.data.some((baixa) => {
                        const hoje = moment().format('YYYY-MM-DD');
                        return hoje >= baixa.data_inicio && hoje <= baixa.data_fim;
                    }),
                };
            }));
            setEmpregados(empregadosComBaixas);
        } catch (error) {
            console.error('Erro ao buscar empregados:', error);
            showAlert('Erro ao carregar empregados.', 'error');
        }
    };

    const fetchBaixasMedicas = async (empregadoId) => {
        const token = localStorage.getItem('access');
        try {
            const response = await axios.get(`${apiBaseURL}/api/baixas-medicas/?empregado=${empregadoId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setBaixasMedicas(response.data);
        } catch (error) {
            console.error('Erro ao buscar baixas médicas:', error);
            showAlert('Erro ao carregar baixas médicas.', 'error');
        }
    };

    const handleOpenViewModal = (empregado) => {
        setSelectedEmpregado(empregado);
        setOpenViewModal(true);
        fetchBaixasMedicas(empregado.id);
    };

    const handleOpenAddModal = (empregado) => {
        setSelectedEmpregado(empregado);
        setNewBaixaMedica({
            data_inicio: moment().format('YYYY-MM-DD'),
            data_fim: moment().format('YYYY-MM-DD'),
            descricao: '',
            documento_medico: null,
        });
        setOpenAddModal(true);
    };

    const handleCloseModals = () => {
        setOpenViewModal(false);
        setOpenAddModal(false);
        setSelectedEmpregado(null);
        setBaixasMedicas([]);
    };

    const handleNewBaixaMedicaChange = (event) => {
        const { name, value } = event.target;
        setNewBaixaMedica((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleFileChange = (event) => {
        setNewBaixaMedica((prevState) => ({
            ...prevState,
            documento_medico: event.target.files[0],
        }));
    };

    const handleAddBaixaMedica = async () => {
        const token = localStorage.getItem('access');
        if (!newBaixaMedica.data_inicio || !newBaixaMedica.data_fim) {
            showAlert('Por favor, preencha todos os campos obrigatórios.', 'warning');
            return;
        }
    
        const formData = new FormData();
        formData.append('empregado', selectedEmpregado.id);
        formData.append('data_inicio', newBaixaMedica.data_inicio);
        formData.append('data_fim', newBaixaMedica.data_fim);
        formData.append('descricao', newBaixaMedica.descricao);
        if (newBaixaMedica.documento_medico) {
            formData.append('documento_medico', newBaixaMedica.documento_medico);
        }
    
        try {
            await axios.post(`${apiBaseURL}/api/baixas-medicas/`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            showAlert('Baixa médica adicionada com sucesso!', 'success');
            setOpenAddModal(false);
            fetchBaixasMedicas(selectedEmpregado.id);
        } catch (error) {
            console.error('Erro ao adicionar baixa médica:', error.response ? error.response.data : error.message);
            if (error.response) {
                console.log('Detalhes do erro:', error.response.data);
            }
            showAlert('Erro ao adicionar baixa médica.', 'error');
        }
    };
    
    const showAlert = (message, severity) => {
        setAlert({ open: true, message, severity });
    };

    const handleCloseAlert = () => {
        setAlert({ ...alert, open: false });
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="h4" gutterBottom>
                Gerenciamento de Baixas Médicas
            </Typography>
            <Paper sx={{ p: 2 }}>
            <TableContainer>
    <Table>
        <TableHead>
            <TableRow>
                <TableCell>Nome Completo</TableCell>
                <TableCell>Ações</TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {empregados.map((empregado, index) => (
                <TableRow
                    key={empregado.id}
                    sx={{
                        backgroundColor: index % 2 === 0 ? 'white' : 'lightgrey', // Alterna entre branco e cinza claro
                    }}
                >
                    <TableCell sx={empregado.isDeBaixa ? { textDecoration: 'underline', color: 'red' } : {}}>
                        {empregado.nome_completo}
                    </TableCell>
                    <TableCell>
                        <IconButton
                            color="primary"
                            onClick={() => handleOpenViewModal(empregado)}
                        >
                            <Visibility />
                        </IconButton>
                        <IconButton
                            color="secondary"
                            onClick={() => handleOpenAddModal(empregado)}
                        >
                            <Add />
                        </IconButton>
                    </TableCell>
                </TableRow>
            ))}
        </TableBody>
    </Table>
</TableContainer>
            </Paper>

            {/* Modal de Visualização de Baixas Médicas */}
            <Modal
                open={openViewModal}
                onClose={handleCloseModals}
                aria-labelledby="modal-view-baixas-medicas"
                aria-describedby="modal-view-baixas-medicas-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 600,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">
                            Baixas Médicas de {selectedEmpregado?.nome_completo}
                        </Typography>
                        <IconButton onClick={handleCloseModals}>
                            <Close />
                        </IconButton>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Data Início</TableCell>
                                    <TableCell>Data Fim</TableCell>
                                    <TableCell>Descrição</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {baixasMedicas.length > 0 ? (
                                    baixasMedicas.map((baixa) => (
                                        <TableRow key={baixa.id}>
                                            <TableCell>{moment(baixa.data_inicio).format('DD/MM/YYYY')}</TableCell>
                                            <TableCell>{moment(baixa.data_fim).format('DD/MM/YYYY')}</TableCell>
                                            <TableCell>{baixa.descricao || 'Não informada'}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            Nenhuma baixa médica registrada para este período.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Modal>

            {/* Modal de Adição de Baixa Médica */}
            <Modal
                open={openAddModal}
                onClose={handleCloseModals}
                aria-labelledby="modal-add-baixa-medica"
                aria-describedby="modal-add-baixa-medica-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">
                            Adicionar Baixa Médica para {selectedEmpregado?.nome_completo}
                        </Typography>
                        <IconButton onClick={handleCloseModals}>
                            <Close />
                        </IconButton>
                    </Box>
                    <TextField
                        label="Data de Início"
                        type="date"
                        name="data_inicio"
                        fullWidth
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        value={newBaixaMedica.data_inicio}
                        onChange={handleNewBaixaMedicaChange}
                        inputProps={{
                            max: moment().format('YYYY-MM-DD'),
                        }}
                    />
                    <TextField
                        label="Data de Fim"
                        type="date"
                        name="data_fim"
                        fullWidth
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        value={newBaixaMedica.data_fim}
                        onChange={handleNewBaixaMedicaChange}
                        inputProps={{
                            max: moment().format('YYYY-MM-DD'),
                        }}
                    />
                    <TextField
                        label="Descrição"
                        name="descricao"
                        fullWidth
                        margin="normal"
                        multiline
                        rows={4}
                        value={newBaixaMedica.descricao}
                        onChange={handleNewBaixaMedicaChange}
                    />
                    <Button
                        variant="contained"
                        component="label"
                        color="primary"
                        fullWidth
                        sx={{ mt: 2 }}
                    >
                        Anexar Documento Médico
                        <input
                            type="file"
                            hidden
                            onChange={handleFileChange}
                        />
                    </Button>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button variant="contained" color="primary" onClick={handleAddBaixaMedica}>
                            Adicionar
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Snackbar de Alertas */}
            <Snackbar
                open={alert.open}
                autoHideDuration={6000}
                onClose={handleCloseAlert}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
                    {alert.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BaixasMedicas;
